import React, { useState, useEffect, useCallback } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import uploadedImage from '../../assets/images/banner.png';
import boxes1 from '../../assets/images/boxes1.png';
import boxes2 from '../../assets/images/boxes2.png';
import whatsappIcon from '../../assets/images/whatsapp_icon.svg';
import callDemoIcon from '../../assets/images/call_demo2.svg';
import helpIcon from '../../assets/images/help.svg';
import redBulletIcon from '../../assets/images/red-bullets.svg';
import { Link } from 'react-router-dom';
import downArrowIcon from '../../assets/images/banner.png';
import { useTranslation } from 'react-i18next';
import { fetchServicePackages } from '../api';
import i18n from '../../i18n';

interface LocalizedString {
  en: string;
  ar: string;
}

interface ServiceItem {
  title: LocalizedString;
  image: string;
  details: {
    subtitle: LocalizedString;
    features: LocalizedString[];
    packages: {
      name: LocalizedString;
      originalPrice: string;
      discountedPrice: string;
    }[];
    packageFeatures: {
      name: LocalizedString;
      gold: boolean;
      premium: boolean;
    }[];
  };
}

const responsive = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 2, slidesToSlide: 1 },
  tablet: { breakpoint: { max: 1024, min: 768 }, items: 1, slidesToSlide: 1 },
  mobile: { breakpoint: { max: 768, min: 0 }, items: 1, slidesToSlide: 1 },
};

const Service: React.FC = () => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [activeService, setActiveService] = useState<ServiceItem | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';

  const localize = (text: LocalizedString) => text[currentLang];

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await fetchServicePackages();
        const mappedServices: ServiceItem[] = data.map(
          (item: any, index: number) => ({
            title: item.title,
            image: index % 2 === 0 ? boxes1 : boxes2,
            details: {
              subtitle: item.title,
              features: item.features || [],
              packages: item.packages.map((pkg: any) => ({
                name: pkg.name,
                originalPrice: pkg.actualPrice,
                discountedPrice: pkg.currentPrice,
              })),
              packageFeatures: item.packages[0].offers.map((offer: any) => {
                const premiumOffer =
                  item.packages[1]?.offers.find(
                    (o: any) =>
                      o.text.en === offer.text.en && o.text.ar === offer.text.ar
                  ) || {};
                return {
                  name: offer.text,
                  gold: offer.common,
                  premium: premiumOffer.common || false,
                };
              }),
            },
          })
        );
        setServices(mappedServices);
        // Set the first service as active by default if there are services
        if (mappedServices.length > 0) {
          setActiveService(mappedServices[0]);
        }
        setLoading(false);
      } catch (err: any) {
        setError('Failed to load services');
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  const scrollToDetails = useCallback(() => {
    const detailsSection = document.getElementById('new-section');
    if (detailsSection) {
      detailsSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        <section className="services">
          <div className="container">
            <div className="row text-center mb-0">
              <div className="col-md-12">
                <div className="content_wrapper">
                  <h2>{t('services')}</h2>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-12">
                <div className="Business_head">
                  <h3>{t('services_package_title')}</h3>
                  <p>{t('services_description')}</p>
                </div>
              </div>
            </div>

            {loading && <p className="text-center">{t('loading')}...</p>}
            {error && <p className="text-center text-danger">{error}</p>}

            <div>
              {!loading && !error && (
                <Carousel
                  responsive={responsive}
                  infinite
                  autoPlay
                  autoPlaySpeed={3000}
                  keyBoardControl
                  showDots={false}
                  arrows={false}
                  containerClass="carousel-container"
                  itemClass="px-3"
                >
                  {services.map((service, index) => (
                    <div
                      className="business_setup_boxes text-center"
                      key={index}
                      onMouseEnter={() => setActiveService(service)}
                    >
                      <h4 className="mb-3">{localize(service.title)}</h4>

                      <div style={{ position: 'relative' }}>
                        <img
                          src={service.image}
                          alt={localize(service.title)}
                          width={300}
                          height={200}
                          className="img-fluid mt-2 rounded-4"
                        />

                        {/* The "View Details" button - now always visible on each service item */}
                        {/* Displays the discounted price of the first package */}
                        {service.details.packages.length > 0 && (
                          <button onClick={scrollToDetails} className="btn_psf">
                            {t('package_start_from')}{' '}
                            {service.details.packages[0].discountedPrice
                              ? `AED ${service.details.packages[0].discountedPrice}`
                              : 'N/A'}
                          </button>
                        )}

                        {/* The hover overlay that still appears on hover */}
                        {activeService === service && isHovered && (
                          <>
                            <img
                              src={downArrowIcon}
                              alt="View Details"
                              style={{
                                position: 'relative',
                                bottom: '0px',
                                left: '42%',
                                transform: 'translateX(-50%) translateY(-35%)',
                                width: '85%',
                                height: '19px',
                                cursor: 'pointer',
                                zIndex: -10,
                              }}
                              onClick={() => {
                                setActiveService(service); // Set active service on click to show details section
                                scrollToDetails();
                              }}
                            />
                            <div
                              className="hover-overlay"
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '400px',
                                height: '300px',
                                backgroundImage: `url(${uploadedImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                padding: '20px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                borderRadius: '10px',
                                zIndex: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <div>
                                  <div style={{ marginBottom: '10px' }}>
                                    <img
                                      src={whatsappIcon}
                                      alt="WhatsApp"
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        marginRight: '10px',
                                      }}
                                    />
                                    <span>{t('Whatsapp_Consultation')}</span>
                                  </div>
                                  <div style={{ marginBottom: '10px' }}>
                                    <img
                                      src={callDemoIcon}
                                      alt="Phone Call"
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        marginRight: '10px',
                                      }}
                                    />
                                    <span>
                                      {t('Book_a_Phone_Consultation')}
                                    </span>
                                  </div>
                                  <div>
                                    <img
                                      src={helpIcon}
                                      alt="Inquire"
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        marginRight: '10px',
                                      }}
                                    />
                                    <span>{t('Inquire_Now')}</span>
                                  </div>
                                </div>
                                <div>
                                  <h3
                                    style={{
                                      color: '#1a73e8',
                                      marginBottom: '10px',
                                    }}
                                  >
                                    {localize(service.details.subtitle)}
                                  </h3>
                                  <ul
                                    style={{
                                      listStyle: 'none',
                                      padding: 0,
                                      color: '#d32f2f',
                                    }}
                                  >
                                    {service.details.features.map(
                                      (feature, idx) => (
                                        <li
                                          key={idx}
                                          style={{ marginBottom: '5px' }}
                                        >
                                          • {localize(feature)}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </Carousel>
              )}
            </div>
          </div>
        </section>

        {activeService && isHovered && (
          <section className="detailed_section" id="new-section">
            <div className="container boxes_details">
              <div className="row">
                <div className="col-md-12 text-center">
                  <div className="main-heading">
                    <h2>{t('SLPO_FREE_ZONE_Business_Zone_Setup_Packages')}</h2>
                    <h5>{localize(activeService.details.subtitle)}</h5>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 drag_info">
                  <div className="business-zone">
                    <div className="social_icon">
                      <div
                        className="icon_wrappes"
                        onClick={() =>
                          window.open('https://wa.me/919904000230', '_blank')
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={whatsappIcon} alt="WhatsApp" />
                        <h5>{t('Whatsapp_Consultation')}</h5>
                      </div>
                      <div
                        className="icon_wrappes"
                        onClick={() =>
                          (window.location.href = 'tel:+919904000230')
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={callDemoIcon} alt="Phone Call" />
                        <h5>{t('Book_a_Phone_Consultation')}</h5>
                      </div>
                      <div className="icon_wrappes ">
                        <Link
                          to="/contact-us"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <img src={helpIcon} alt="Inquire" />
                          <h5>{t('Inquire_Now')}</h5>
                        </Link>
                      </div>
                    </div>
                    <div className="details_info">
                      <ul>
                        {activeService.details.features.map((feature, idx) => (
                          <li key={idx} style={{ fontSize: '19px' }}>
                            {localize(feature).length > 20
                              ? localize(feature).substring(0, 20) + '...'
                              : localize(feature)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="reachout_desk">
                    <p>
                      <span className="large_font">“{t('Reach_out_now')} </span>
                      {t(
                        'our_expert_consultants_are_just_a_call_or_text_away_ready'
                      )}
                      {t('to_walk_you_through_the_entire_formation_process.')}”
                    </p>
                  </div>
                  <div className="total_buttons">
                    <div className="terms-condition">
                      <Link to="../terms-conditions">*{t('T&C Apply')}</Link>
                    </div>
                    <div className="view_more">
                      <Link to="#">{t('View More')}</Link>
                    </div>
                    <div className="started_today">
                      <Link to="#">{t('Get Started Today')}</Link>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="package_details mt-3">
                    <div className="package_info">
                      <div className="main-title">
                        <h5>{t('Packages_Starting_from')}</h5>
                      </div>
                    </div>
                    <div className="price_strike d-flex g-20">
                      {activeService.details.packages.map((pkg, idx) => (
                        <div className="title" key={idx}>
                          <h3>AED {pkg.originalPrice}</h3>
                          <h5>AED {pkg.discountedPrice}</h5>
                          <a href="#">{localize(pkg.name)}</a>
                        </div>
                      ))}
                    </div>
                  </div>
                  <table className="table">
                    <tbody>
                      {activeService.details.packageFeatures.map(
                        (feature, idx) => (
                          <tr key={idx}>
                            <th scope="row" className="first_wrappes">
                              <img
                                src={redBulletIcon}
                                alt="Bullet"
                                style={{ width: '25px', height: '25px' }}
                              />
                            </th>
                            <td className="second_wrappes">
                              {localize(feature.name)}
                            </td>
                            <td>
                              <span
                                className="d-inline-flex justify-content-center align-items-center rounded-circle"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: feature.gold
                                    ? '#00ba00'
                                    : '#ff4141',
                                  color: '#fff',
                                }}
                              >
                                <i
                                  className={`bi ${feature.gold ? 'bi-check-lg' : 'bi-x-lg'}`}
                                  style={{ fontSize: '12px' }}
                                ></i>
                              </span>
                            </td>
                            <td>
                              <span
                                className="d-inline-flex justify-content-center align-items-center rounded-circle"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: feature.premium
                                    ? '#00ba00'
                                    : '#ff4141',
                                  color: '#fff',
                                }}
                              >
                                <i
                                  className={`bi ${feature.premium ? 'bi-check-lg' : 'bi-x-lg'}`}
                                  style={{ fontSize: '12px' }}
                                ></i>
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
      <style>{`
      .boxes_details {
box-shadow: 0px 0px 20px 20px #00000033;
border-radius: 12px;
background-color: #fff;
}
.btn_psf {
  position: absolute;
  bottom: 20px;
  transition: bottom 0.3s ease;
}

.btn_psf:hover {
  bottom: 35px; /* moves UP by 15px */
}


`}</style>

      {currentLang === 'ar' && (
        <style>{`
          .services .Business_head h3 {
            text-align: end;
          }
          .details_info ul li::after{
          left: 130px;}
        `}</style>
      )}
    </>
  );
};

export default Service;
